import React, { memo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { EditTimeCardModal } from 'Components/TimeTracking';

import {
  updateTimeCard,
  listTimecardTypes,
  listAllEmployeesForModals,
  listAllProjectsForModals,
} from 'Containers/TimeTracking/actions';

import { useTimeTrackingFunctions } from 'Context/TimeTracking';
import { updateTimeCardErrorSelector, timecardTypesSelector } from 'Containers/TimeTracking/selectors';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';

const EditTimeCardModalContainer = () => {
  const dispatch = useDispatch();

  const [timeIn, setTimeIn] = useState(undefined);
  const [timeOut, setTimeOut] = useState(undefined);
  const [selectedTypeId, setSelectedTypeId] = useState<number | undefined>(undefined);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(undefined);
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(undefined);

  const {
    isEditTimeCardModalOpen,
    setIsEditTimeCardModalOpen,
    selectedTimeCard,
    employeeDropdownItems,
    projectDropdownItems,
  }: any = useTimeTrackingFunctions();

  const errors = useSelector(updateTimeCardErrorSelector, areEqual);
  const timecardTypes = useSelector(timecardTypesSelector, areEqual);
  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);

  // Load timecard types, employees, and projects when modal opens
  useEffect(() => {
    if (isEditTimeCardModalOpen && firstCompanyId) {
      dispatch(listTimecardTypes());
      dispatch(listAllEmployeesForModals(firstCompanyId));
      dispatch(listAllProjectsForModals(firstCompanyId));
    }
  }, [isEditTimeCardModalOpen, firstCompanyId]);

  useEffect(() => {
    if (selectedTimeCard?.timeIn && selectedTimeCard?.timeOut) {
      setTimeIn(new Date(selectedTimeCard.timeIn));
      setTimeOut(new Date(selectedTimeCard.timeOut));
    }
    if (selectedTimeCard?.timecardType?.id) {
      setSelectedTypeId(selectedTimeCard.timecardType.id);
    }
    if (selectedTimeCard?.userId) {
      setSelectedEmployeeId(Number(selectedTimeCard.userId));
    }
    if (selectedTimeCard?.projectId) {
      setSelectedProjectId(Number(selectedTimeCard.projectId));
    }
  }, [selectedTimeCard]);

  const resetInputs = () => {
    if (selectedTimeCard?.timeIn && selectedTimeCard?.timeOut) {
      setTimeIn(new Date(selectedTimeCard.timeIn));
      setTimeOut(new Date(selectedTimeCard.timeOut));
    }
    if (selectedTimeCard?.timecardType?.id) {
      setSelectedTypeId(selectedTimeCard.timecardType.id);
    }
    if (selectedTimeCard?.userId) {
      setSelectedEmployeeId(Number(selectedTimeCard.userId));
    }
    if (selectedTimeCard?.projectId) {
      setSelectedProjectId(Number(selectedTimeCard.projectId));
    }
  };

  const closeModal = () => {
    setIsEditTimeCardModalOpen(false);
    resetInputs();
  };

  const onEditButtonClick = useCallback(() => {
    dispatch(
      updateTimeCard(
        Number(selectedTimeCard.id),
        selectedTypeId,
        selectedEmployeeId,
        selectedProjectId,
        timeIn?.toISOString(),
        timeOut?.toISOString(),
        closeModal
      )
    );
  }, [timeIn, timeOut, selectedTimeCard, selectedTypeId, selectedEmployeeId, selectedProjectId]);

  return (
    <EditTimeCardModal
      // modal variables
      isOpen={isEditTimeCardModalOpen}
      formErrors={errors}
      canEdit={timeIn && timeOut && selectedTypeId && selectedEmployeeId && selectedProjectId}
      // employee variables
      employees={employeeDropdownItems || []}
      selectedEmployeeId={selectedEmployeeId}
      setSelectedEmployee={setSelectedEmployeeId}
      // project variables
      projects={projectDropdownItems || []}
      selectedProjectId={selectedProjectId}
      setSelectedProject={setSelectedProjectId}
      // type variables
      timecardTypes={timecardTypes}
      selectedTypeId={selectedTypeId}
      onTypeChange={setSelectedTypeId}
      // date variables
      startDate={timeIn}
      endDate={timeOut}
      // modal functions
      modalCloseClick={closeModal}
      onEditButtonClick={onEditButtonClick}
      // date functions
      onChangeStartDate={setTimeIn}
      onChangeEndDate={setTimeOut}
    />
  );
};

const EditTimeCardModalContainerMemo = memo(EditTimeCardModalContainer, areEqual);

export { EditTimeCardModalContainerMemo as EditTimeCardModal };
